import { useEffect, useMemo, useState } from 'react';

import { Button, SubmitButton, Typography } from '@capsule/consumer-design-system';
import type { CheckboxButtonGroupOption } from '@capsule/consumer-design-system/primitives/Form/Checkbox/CheckboxButtonGroup/CheckboxButtonGroup';

import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import LogRocket from 'logrocket';
import { useHistory } from 'react-router';
import { css } from 'styled-components';
import * as Yup from 'yup';

import Spinner from 'src/app/components/Elements/Spinner';
import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import type {
  HealthProfileMedicationToVerifyGroup,
  HealthProfileMedicationToVerifyResult,
  HealthProfileMedicationTransferrable,
  SearchHealthProfileMedicationToVerify,
} from 'src/app/hooks/data/healthProfile';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { loggedInHealthProfile } from 'src/app/router/paths';
import type { MedicationSearchResult } from 'src/app/types';
import { FORM_VALIDATION_ERRORS } from 'src/consts';

import type { HealthProfileFormProps } from '..';
import { HealthProfileUpdateStepNames } from '../constants';
import { MedicationSearchPicker } from '../medicationSearchPicker';
import { MedicationsCheckBoxPicker } from '../medicationsPicker';
import { NegativeMarginOffset, GreenFont } from '../styles';
import {
  convertSearchToHealthProfileMedication,
  formatMedicationDescriptionWithStrengthLabelAndCapsule,
  formatMedicationWithStrengthAndLabel,
  getMedicationsTransferEligibility,
  replaceDotWithUnicode,
} from '../utils';

import { QuestionDialog } from './questionDialog';

const FORM_ID = 'medicationPickerForm';

const validationSchema = Yup.object().shape({
  form: Yup.array().test('form', FORM_VALIDATION_ERRORS.selectMedicationsPicker, function () {
    const options = Object.values(this.parent);
    const searchMeds = this.parent.otherMeds;
    // When no options are selected, we're left with a 1-length array with a single empty nested array.
    return options.filter(value => !!value).length > 1 || searchMeds.length > 0;
  }),
});

export type MedicationsFormValueType = Partial<
  Record<string, any> & {
    otherMeds: MedicationSearchResult[];
  }
>;

type ExtendedButtonPick = Pick<CheckboxButtonGroupOption, 'label' | 'value' | 'description'> & {
  lastFillDate?: string;
  isCapsuleMed: boolean;
};

type HealthProfileFormGroup = {
  headerName: string | null;
  options: ExtendedButtonPick[];
};

export const MedicationPicker = (
  props: HealthProfileFormProps & {
    zipCode?: string;
    isLoading: boolean;
    healthProfileMedications?: HealthProfileMedicationToVerifyGroup[];
    source?: string;
    updateMedications: (allSelectedMeds: HealthProfileMedicationTransferrable[], patientId: string) => Promise<void>;
  }
) => {
  const {
    onToggleHideHeader,
    zipCode,
    healthProfileMedications,
    healthProfilePersistentData,
    setHealthProfilePersistentData,
    isLoading,
    source,
    updateMedications,
  } = props;

  useEffect(() => {
    onToggleHideHeader(true);
  }, [onToggleHideHeader]);

  const stepName = HealthProfileUpdateStepNames.SelectTakingMedications;
  const history = useHistory();

  // All medications available, flattened into one array. We'll use this for analytics and some functionality
  const allHealthProfileMedicationsToVerify = useMemo(() => {
    return healthProfileMedications?.map(group => group.medications).flat();
  }, [healthProfileMedications]);

  const capsuleMedsShown = allHealthProfileMedicationsToVerify
    ?.filter(med => med.isCapsuleMed)
    .map(med => med.primaryName);

  const hasOnlyCapsuleMeds =
    (allHealthProfileMedicationsToVerify?.length ?? 0) !== 0 &&
    (allHealthProfileMedicationsToVerify?.length ?? 0) === (capsuleMedsShown?.length ?? 0);

  // Set form options with the medications groups from the BFF
  const { medicationsFormOptions, medicationsFormValues } = useMemo(() => {
    /*
     * If we have medications from the BFF, populate the form options
     * for CheckboxButtonGroup and the initial values for the form.
     */
    const emptyFormAndOptions = {
      medicationsFormOptions: [] as HealthProfileFormGroup[],
      medicationsFormValues: { otherMeds: [] as MedicationSearchResult[] } as MedicationsFormValueType,
    };
    return (
      healthProfileMedications?.reduce(
        (
          acc: {
            medicationsFormOptions: HealthProfileFormGroup[];
            medicationsFormValues: MedicationsFormValueType;
          },
          group: HealthProfileMedicationToVerifyGroup
        ) => {
          const medications = group.medications;

          acc.medicationsFormOptions.push({
            headerName: group?.headerName,
            options: medications.map(medication => ({
              // The `replaceDotWithUnicode` function replaces the "." with unicode "․" because Formik considers "." as a property accessor
              label: replaceDotWithUnicode(medication.primaryName),
              // Use med name with strength and label as value and form key to avoid selecting meds with the same name but different dosages
              value: replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(medication)),
              description: formatMedicationDescriptionWithStrengthLabelAndCapsule(medication),
              lastFillDate: medication.lastFillDate,
              isCapsuleMed: medication.isCapsuleMed,
            })),
          });
          /*
           * Form values are an object with medication name to boolean. Here we're pre-checking
           * medications that are already in persistent storage.
           */
          const obj = Object.fromEntries(
            medications.map(medication => [
              replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(medication)),
              hasOnlyCapsuleMeds
                ? true // If we only have Capsule Meds, pre-check all of them
                : !!healthProfilePersistentData.selectedMeds?.find(
                    persistedMed =>
                      replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(persistedMed)) ===
                      replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(medication))
                  ),
            ])
          );
          acc.medicationsFormValues = { ...acc.medicationsFormValues, ...obj };

          return acc;
        },
        emptyFormAndOptions
      ) ?? emptyFormAndOptions
    );
  }, [healthProfileMedications]);

  return isLoading ? (
    <Spinner centered />
  ) : (
    <Formik
      validationSchema={!hasOnlyCapsuleMeds && validationSchema}
      id={FORM_ID}
      onSubmit={async (values, formikHelpers) => {
        const { setSubmitting } = formikHelpers;
        const getMedicationsFromForm = (
          selectedMeds: Record<string, boolean>
        ): HealthProfileMedicationToVerifyResult[] =>
          /*
           * 1. Get all non-false values from the selectedMeds. In other words, only the fields actually selected by the user
           * 2. Match the resulting values with the data from BFF. We need to persist the transfer eligibility attributes for next steps
           * 3. Clear out undefined values from that. This shouldn't happen, since that's where we populate the selectedMeds options. It
           * is here mostly to placate the TS compiler.
           */
          Object.entries(selectedMeds)
            .filter(([, value]) => !!value)
            .map(([key]) =>
              allHealthProfileMedicationsToVerify?.find(
                med => key !== 'Other' && replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(med)) === key
              )
            )
            .filter(med => med !== undefined);

        setSubmitting(true);
        try {
          const { otherMeds, ...selectedMeds } = values as MedicationsFormValueType;

          /*
           * The checkbox group will generate form values { [key: string] : boolean }, and we'll have the
           * HealthProfileMedicationToVerifyResult objects available from the query. So we pull the
           * objects for the truthy values and persist, since we'll have to know medication eligibility
           * for transfer in an upcoming step. To this we concatenate the "otherMeds" array, which is a list of
           * meds selected by the patient through the search medication element. For those, we have to add some
           * baseline data that we don't get from Medication Search API and are required for transfer task creation.
           *
           * Our partners in Product will take care of informing PX about the "contact customer" part.
           */
          const otherMedsSelected = otherMeds?.map((med: MedicationSearchResult) =>
            convertSearchToHealthProfileMedication(med)
          ) as SearchHealthProfileMedicationToVerify[];

          const medsFromForm = getMedicationsFromForm(selectedMeds).filter(
            (med): med is HealthProfileMedicationToVerifyResult => med !== undefined
          );

          const allSelectedMeds = [...medsFromForm, ...otherMedsSelected];
          healthProfilePersistentData.selectedMeds = allSelectedMeds;
          await setHealthProfilePersistentData(healthProfilePersistentData);

          // If user has only capsule meds, push to the dedicated search medication step
          if (hasOnlyCapsuleMeds) {
            history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.SearchMedications }));
            setSubmitting(false);
            return;
          }

          // Otherwise, update the medications and continue with the regular flow
          updateMedications();
          /*
           * Redirect logic for next steps:
           * - If the user selects any unrestricted medications, go to the Move Your Meds step
           *    Unrestricted eligible medications are those that:
           *      1. Are not filled by Capsule
           *      2. Do not require a doctor for transfer
           *      3. Are not otherwise ineligible for transfer (e.g.: part of Stop Fill list)
           *
           * - If the user selects only medications that require a doctor for transfer, go to the
           *   "Just Say Capsule" step
           *
           * - If the user selects only medications that are filled by Capsule or fully ineligible
           *   for transfer, go to the Got It step
           *
           * - If there are no selected medications at all, go to the Got It step
           */

          const { eligibleMeds, ineligibleMeds, doctorRequiredMeds, capsuleMeds } = getMedicationsTransferEligibility(
            healthProfilePersistentData.selectedMeds
          );
          if (ineligibleMeds.length > 0) {
            LogRocket.track('RX Live - At Least One Med Ineligible For Transfer');
          }
          if (eligibleMeds.length > 0) {
            LogRocket.track('RX Live - At Least One Med Eligible For Transfer');
          }

          if (hasOnlyCapsuleMeds) {
            history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.SearchMedications }));
          } else if (eligibleMeds.length > 0) {
            history.push(
              loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.ConfirmMoveMedications })
            );
          } else if (
            allSelectedMeds.length === 0 || // no meds selected
            (doctorRequiredMeds.length === 0 && (capsuleMeds.length > 0 || ineligibleMeds.length > 0)) // all meds are Capsule-filled or ineligible
          ) {
            history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.GotIt }));
          } else {
            // this should leave only doctor-required meds
            history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.JustSayCapsule }));
          }
        } catch (e) {
          console.error('Error submitting form', e);
        } finally {
          setSubmitting(false);
        }
      }}
      initialValues={medicationsFormValues}
      enableReinitialize
    >
      {(formik: FormikProps<MedicationsFormValueType>) => {
        const { isSubmitting, errors, touched, submitCount } = formik;

        if (errors?.form && submitCount > 0) {
          // `form` error detects whether anything is selected. If *nothing* is selected, we gotta map the error
          // out to the FormikCheckboxButtonGroup options so they display as errored. Also have to override the
          // "touched" property, otherwise only items that were selected and unselected will display in red.
          Object.assign(
            touched,
            ...medicationsFormOptions
              .map(group => group.options)
              .flat()
              .map(medication => ({ [medication.label]: true }))
          );
          Object.assign(
            errors,
            ...medicationsFormOptions
              .map(group => group.options)
              .flat()
              .map(medication => ({ [medication.label]: true }))
          );
        }

        const trackViewPayload = {
          source: source || 'organic_navigation',
          num_total_medications_90_days:
            (healthProfileMedications && healthProfileMedications[0].medications.length) || 0,
          'num_total_medications_90-180_days':
            (healthProfileMedications && healthProfileMedications[1].medications.length) || 0,
          num_capsule_meds: capsuleMedsShown?.length || 0,
          num_noncapsule_meds: allHealthProfileMedicationsToVerify?.filter(med => !med.isCapsuleMed).length || 0,
          num_total_medications_shown: allHealthProfileMedicationsToVerify?.length || 0,
          medication_names: allHealthProfileMedicationsToVerify?.map(med => med.primaryName) || [],
        };

        return (
          <>
            <SimpleFeatureLayout
              css={css`
                margin-top: ${({ theme }) => theme.space.s6};
              `}
              shouldDisplayCapsuleLogo
              backButton={{ isRendered: false }}
              title={
                <Typography
                  variant={{ base: 'displayXl', smallDown: 'heading2' }}
                  css={css`
                    margin-top: ${({ theme }) => theme.space.s6};
                  `}
                >
                  {hasOnlyCapsuleMeds || allHealthProfileMedicationsToVerify?.length === 0
                    ? `Confirm the medications you currently take`
                    : `Your pharmacist needs you to verify your medications`}
                </Typography>
              }
              description={
                hasOnlyCapsuleMeds || allHealthProfileMedicationsToVerify?.length === 0
                  ? `Let’s make sure your health profile is up-to-date.`
                  : `Which of these medications do you currently take?`
              }
              keyName={stepName}
              body={{
                content: (
                  <>
                    <TrackViewHealthProfileTransfer {...trackViewPayload} />
                    <NegativeMarginOffset />
                    {
                      /*
                       * BFF sends in medications grouped by whatever logic, with a headerName for each
                       * group. We just render a MedicationsCheckBoxPicker for each group. In case there
                       * are no meds in the group, don't render it. If the header name is empty, we just
                       * don't include a header. This will allow us to render a single group including
                       * all meds if the logic changes, for example.
                       */
                      medicationsFormOptions.map((group: HealthProfileFormGroup) => {
                        return (
                          group.options.length > 0 && (
                            <>
                              <GreenFont>
                                {group.headerName && <Typography variant="overline">{group.headerName}</Typography>}
                              </GreenFont>
                              <MedicationsCheckBoxPicker
                                medicationsFormOptions={group.options}
                                formId={FORM_ID}
                                formik={formik}
                              />
                            </>
                          )
                        );
                      })
                    }

                    {!hasOnlyCapsuleMeds && (
                      <SwitchMedicationsSearchPicker
                        formik={formik}
                        zipCode={zipCode || ''}
                        hasAnyFormMeds={!!allHealthProfileMedicationsToVerify?.length}
                      />
                    )}

                    <div
                      css={css`
                        display: ${errors.form && submitCount > 0 ? 'block' : 'none'};
                        color: ${({ theme }) => theme.colors.capsuleRed60};
                      `}
                    >
                      <Typography variant="small">Please select at least one medication.</Typography>
                    </div>
                    <SubmitButton variant="contained-secondary-lg" isSubmitting={isSubmitting} form={FORM_ID}>
                      {hasOnlyCapsuleMeds ? `Continue` : `Submit`}
                    </SubmitButton>
                    {!hasOnlyCapsuleMeds && (
                      <Button
                        variant="text-md"
                        onClick={() => {
                          updateMedications();
                          history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.GotIt }));
                        }}
                      >
                        {`I don’t take ${allHealthProfileMedicationsToVerify?.length === 0 ? 'other' : 'these'} meds`}
                      </Button>
                    )}
                    {!hasOnlyCapsuleMeds && <QuestionDialog />}
                  </>
                ),
              }}
              // @ts-ignore: this is definitely a string
              error={errors?.form}
            />
          </>
        );
      }}
    </Formik>
  );
};

interface SwitchMedicationsSearchPickerProps {
  formik: FormikProps<MedicationsFormValueType>;
  zipCode: string;
  hasAnyFormMeds: boolean;
}

const SwitchMedicationsSearchPicker = (props: SwitchMedicationsSearchPickerProps) => {
  const { hasAnyFormMeds } = props;

  const [isSearchPickerEnabled, setIsSearchPickerEnabled] = useState(!hasAnyFormMeds);

  return isSearchPickerEnabled ? (
    <MedicationSearchPicker {...props} formId={FORM_ID} />
  ) : (
    <GreenFont>
      <Typography
        as="button"
        css={css`
          all: unset;
          justify-content: start;
        `}
        variant="body"
        onClick={() => {
          setIsSearchPickerEnabled(true);
        }}
      >
        + Add other medications
      </Typography>
    </GreenFont>
  );
};

interface TrackViewHealthProfileTransferPayload {
  source: string;
  num_total_medications_90_days: number;
  'num_total_medications_90-180_days': number;
  num_capsule_meds: number;
  num_noncapsule_meds: number;
  num_total_medications_shown: number;
  medication_names: string[];
}

const TrackViewHealthProfileTransfer = (payload: TrackViewHealthProfileTransferPayload) => {
  useSegmentEvent('viewHealthProfileOtherMeds', {
    shouldTrackOnMount: true,
    payload,
  });

  return null;
};
